import { AxiosError } from 'axios';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// import { actionLog } from "services/actionlog.service";
import { fetchUser, signInRequest } from 'services/auth.service';
import {
  // updateUserProfile,
  updateUserStatus,
  // uploadPicture,
} from 'services/profile.service';
import { clearFlowSessionState } from 'pages/MinhaEquipe/ApreciarRealizacao/shared/eligibility';
import { CyclesInterface, RealCycle, User, UserInfoInterface } from 'types/user';
import { clearStorage, getStorageItem, removeStorageItem, setStorageItem } from 'utils/storage';
import { clearSessionReads } from 'utils/session-reads';

import { api } from '../../services/api';
import { setAuthorizationHeader } from '../../services/interceptors';
import { createTokenCookies, getToken, removeTokenCookies } from '../../utils/tokenCookies';
import { getPlayerInfo, getServerTime, getTaskCycles } from 'services/dashboard.service';
import { GetVersion } from 'services/utils';
import { ActionLog } from 'types/funifier';
import { getActiveGame } from 'services/actionlog.service';
import { MIN_CYCLE_YEAR, toCyclesExtended, toRealCycles } from 'utils/calendar-cycles';

interface SignInCredentials {
  username: string;
  password: string;
  profile?: { url: string };
}

interface AuthContextData {
  login: (credentials: SignInCredentials) => Promise<void | AxiosError>;
  loginWithToken: (access_token: string) => Promise<void>;
  signOut: (pathname?: string) => void;
  refreshUser: () => void;
  refreshUserPromise: () => Promise<void>;
  user?: User;
  userInfo: UserInfoInterface | undefined;
  isAuthenticated: boolean;
  loadingUserData: boolean;
  isMSTeams: boolean;
  cycles: CyclesInterface[] | undefined;
  currentCycle: CyclesInterface | undefined;
  realCycle: RealCycle[] | undefined;
  cyclesExtended: RealCycle[] | undefined;
  setRealCycle: (data?: any) => void;
  cycleSelected: { _id: string; title: string; isV2?: boolean; version: string } | undefined;
  setCycleSelected: (data?: any) => void;
  showNotifications: boolean;
  setShowNotifications: (data: boolean) => void;
  activeGame: { drawGame?: string | undefined; played: boolean; interaction: boolean } | undefined;
  setActiveGame: (data: { drawGame?: string | undefined; played: boolean; interaction: boolean } | undefined) => void;
  loadGame: () => Promise<void>;
  loadingInfo: boolean;
  /** Sobe 1 a cada `refreshUser` concluído: dependência primitiva para quem relê depois dele. */
  userRevision: number;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const token = getToken();

  const isAuthenticated = !!token;

  const [user, setUser] = useState<User | undefined>();
  const [userInfo, setUserInfo] = useState<UserInfoInterface | undefined>();
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [cycles, setCycles] = useState<CyclesInterface[] | undefined>();
  const [realCycle, setRealCycle] = useState<RealCycle[] | undefined>();
  const [cyclesExtended, setCyclesExtended] = useState<RealCycle[] | undefined>();
  const [isMSTeams, setIsMSTeams] = useState<boolean>(false);
  const [cycleSelected, setCycleSelected] = useState<{ _id: string; title: string; version: string } | undefined>();
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeGame, setActiveGame] = useState<{ drawGame?: string | undefined; played: boolean; interaction: boolean }>();
  const [loadingInfo, setLoadingInfo] = useState(false);
  // Ciclo da última busca do userInfo. Entre gravar o ciclo e o efeito que busca, `loadingInfo` ainda é `false`: sem
  // isto, a tela via por um quadro o ciclo novo com o userInfo de antes (ou nenhum) e mostrava o estado vazio.
  const [cicloDoUserInfo, setCicloDoUserInfo] = useState<string>();
  const [userRevision, setUserRevision] = useState(0);

  // Lidos pelo refreshUser sem virar dependência dele: a referência precisa ser estável, senão
  // quem o tem nas deps de um efeito entra em laço (DCE-01).
  const cycleTitleRef = useRef<string>();
  cycleTitleRef.current = cycleSelected?.title;
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const refreshQueuedRef = useRef<Promise<void> | null>(null);
  const hasUserInfoRef = useRef(false);
  hasUserInfoRef.current = !!userInfo;

  const currentCycle = useMemo(() => {
    if (!cycles) return undefined;
    const cycle = cycles.find((item: any) => item.status === 'open');

    return !!cycle ? cycle : cycles[0];
  }, [cycles]);

  const signOut = useCallback((pathname?: string) => {
    removeTokenCookies();
    clearStorage();
    // clearStorage só limpa o localStorage; a escolha de fluxo/caches da apreciação
    // vivem em sessionStorage (navegação ativa) e precisam de limpeza explícita —
    // o logout MSAL mantém a aba viva e outro usuário poderia herdar a escolha.
    clearFlowSessionState();
    clearSessionReads();
    setAuthorizationHeader(api.defaults, '');
    setUser(undefined);
    setUserInfo(undefined);
    setRealCycle(undefined);
    setCyclesExtended(undefined);
    setCycles(undefined);
    setLoadingUserData(false);
    //   navigate(pathname || '/login');
  }, []);

  async function login({ username, password }: SignInCredentials) {
    try {
      const auth = await signInRequest({ username, password });

      if (auth.message) {
        throw auth;
      }

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('quizV2Minimized');
      }

      // Login novo = sessão nova de apreciação: não herda escolha de fluxo/caches de
      // elegibilidade de um usuário anterior na mesma aba.
      clearFlowSessionState();
      clearSessionReads();

      const { access_token, refreshToken, permissions, roles } = auth;

      createTokenCookies(access_token, refreshToken);
      setAuthorizationHeader(api.defaults, access_token);

      // O userInfo sai do efeito do ciclo, já com o ciclo (DCE-05).
      const _user = await fetchUser();

      const userData = { _id: _user._id, ..._user, permissions, roles };

      setUser(userData);

      return userData;
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      throw err;
    }
  }

  async function loginWithToken(access_token: string) {
    try {
      if (!access_token) {
        return;
      }

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('quizV2Minimized');
      }

      // Login novo (SSO) = sessão nova de apreciação: não herda escolha de fluxo/caches.
      clearFlowSessionState();
      clearSessionReads();

      createTokenCookies(access_token, '');
      setAuthorizationHeader(api.defaults, access_token);

      // O userInfo sai do efeito do ciclo, já com o ciclo (DCE-05).
      const _user = await fetchUser();

      const { name, image, extra } = _user;

      const userData = {
        _id: _user._id,
        ..._user,
        extra,
        name,
        image,
        permissions: null,
        roles: null,
      };

      setUser(userData);

      return userData;
    } catch (error) {
      const err = error as AxiosError;
      // console.log(err);
      throw err;
    }
  }

  // O find/info vai com o ciclo selecionado (DCE-07).
  const runRefresh = useCallback(() => {
    const refresh = (async () => {
      const updatedUser = await updateUserStatus();
      const _userInfo = await getPlayerInfo(updatedUser._id, cycleTitleRef.current);

      setUserInfo(_userInfo);
      setUser(updatedUser);
      setUserRevision(revision => revision + 1);
    })().finally(() => {
      refreshInFlightRef.current = null;
    });

    refreshInFlightRef.current = refresh;
    return refresh;
  }, []);

  // Quem chama logo depois de uma escrita precisa de um PUT que saia depois dela: a rodada em
  // andamento pode ter lido o servidor antes. Por isso a chamada feita durante uma rodada espera
  // ela terminar e faz mais uma, e todas as chamadas desse intervalo dividem essa rodada seguinte:
  // uma rajada (dois websockets, volta à Home) faz no máximo 2 PUT de status (DCE-24, DCE-58).
  const refreshUserPromise = useCallback(() => {
    const inFlight = refreshInFlightRef.current;
    if (!inFlight) return runRefresh();

    if (!refreshQueuedRef.current) {
      refreshQueuedRef.current = inFlight
        .catch(() => undefined)
        .then(() => {
          refreshQueuedRef.current = null;
          return runRefresh();
        });
    }
    return refreshQueuedRef.current;
  }, [runRefresh]);

  const refreshUser = refreshUserPromise;

  // Único publicador do userInfo. Falha antes do primeiro userInfo encerra a sessão, como
  // fazia a falha do fetchUser (DCE-51); depois dele, mantém o que já está na tela.
  const loadUserInfo = useCallback(
    async (playerId: string, cycleTitle?: string) => {
      setLoadingInfo(true);
      try {
        setUserInfo(await getPlayerInfo(playerId, cycleTitle));
      } catch (error) {
        if (!hasUserInfoRef.current) signOut();
      } finally {
        setLoadingInfo(false);
        setCicloDoUserInfo(cycleTitle);
      }
    },
    [signOut],
  );

  // useEffect(() => {
  // 	if (!token) signOut(pathname);
  // }, [pathname, token, signOut]);

  useEffect(() => {
    const token = getToken();

    async function getUserData() {
      setLoadingUserData(true);

      try {
        const response = await fetchUser();

        if (response) {
          const { _id, name, image, email, permissions, roles } = response;

          // O userInfo sai do efeito do ciclo, já com o ciclo (DCE-05, DCE-08).
          setUser({ ...response, _id, name, image, email, permissions, roles });
        }
      } catch (error) {
        signOut();
      }

      setLoadingUserData(false);
    }

    if (!user && token) {
      setAuthorizationHeader(api.defaults, token);
      getUserData();
    }

    function handleSignoutEvent() {
      signOut();
    }

    document.addEventListener('signout', handleSignoutEvent, false);

    const _MSTeams = getStorageItem('isMSTeams');

    if (!!_MSTeams && _MSTeams === 'true') {
      setIsMSTeams(true);
    }

    return () => {
      document.removeEventListener('signout', handleSignoutEvent);
    };
  }, [user, signOut]);

  useEffect(() => {
    async function load(playerId: string) {
      // O calendário traz ciclo e trimestre em cada fase: os ciclos saem dele, sem get_cycles e
      // get_cycles_extended (DCE-59). O "agora" é o relógio do servidor, nunca o do navegador
      // (DCE-62). Falha de qualquer das duas leituras vira calendário vazio e segue sem ciclo (DCE-64).
      const [taskCycleResults, serverNow] = await Promise.all([getTaskCycles(), getServerTime()]).catch(
        (): [CyclesInterface[], number] => [[], 0],
      );
      setCycles(taskCycleResults);

      const hasStarted = (item: RealCycle) => item.date_start <= serverNow;

      // `toCyclesExtended` já corta pelo ano; `get_cycles` não cortava, então o corte vem aqui.
      const startedCycles = toRealCycles(taskCycleResults).filter(item => hasStarted(item) && item.year >= MIN_CYCLE_YEAR);
      const startedExtendedCycles = toCyclesExtended(taskCycleResults, serverNow).filter(hasStarted);

      setRealCycle(startedCycles);
      setCyclesExtended(startedExtendedCycles);

      if (startedCycles.length) {
        const cycle = startedCycles.find(item => item.status === 'open') ?? startedCycles[0];

        setCycleSelected({
          _id: cycle._id,
          title: cycle.title,
          version: GetVersion(cycle.date_start),
        });
      } else {
        // Sem ciclo para escolher, o efeito do ciclo nunca publicaria: vai sem ciclo (DCE-53).
        loadUserInfo(playerId);
      }
    }

    if (user?._id && !cycles) {
      load(user._id);
    }
  }, [cycles, user?._id, loadUserInfo]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const sender = searchParams.get('invite');
    const teamsContext = searchParams.get('teamsContext');

    if (!!sender?.match(/^F\d+$/gi)) {
      setStorageItem('jogajuntobb_invite_code', sender);
    }

    if (!!teamsContext && teamsContext === 'true') {
      setStorageItem('isMSTeams', 'true');
      setIsMSTeams(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (user?._id && cycleSelected?.title) loadUserInfo(user._id, cycleSelected.title);
  }, [cycleSelected?.title, user?._id, loadUserInfo]);

  // Só os jogos da Festa Junina leem `activeGame`, e nenhuma rota os monta: quem religar um deles
  // chama `loadGame` na montagem (DCE-63).
  async function loadGame() {
    setActiveGame(undefined);

    // setActiveGame({drawGame: 'raspadinha', played: false, interaction: true}); // local teste, comenta as duas abaixo
    const game = await getActiveGame();
    setActiveGame(game);
  }

  return (
    <AuthContext.Provider
      value={{
        refreshUserPromise,
        isAuthenticated,
        user,
        userInfo,
        loadingUserData,
        cycles,
        realCycle,
        cyclesExtended,
        setRealCycle,
        currentCycle,
        isMSTeams,
        login,
        loginWithToken,
        signOut,
        refreshUser,
        cycleSelected,
        setCycleSelected,
        showNotifications,
        setShowNotifications,
        activeGame,
        setActiveGame,
        loadGame,
        loadingInfo: loadingInfo || (!!user?._id && !!cycleSelected?.title && cicloDoUserInfo !== cycleSelected.title),
        userRevision,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
